import {useCallback, useEffect, useState} from 'react'
import axios from 'axios'
import {useReduxAuth} from '../../../auth'
import {toast} from 'react-toastify'
import DataTableList from '../../components/DataTableList'
import {TIME_TRACKED_HEADERS} from '../../../../constants/RoleConstants'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import MetaTags from '../../../../meta/MetaTags'
import DeleteConfirmationModal from '../../components/modals/DeleteConfirmationModal'
import EditPatientTrackedTime from '../../components/modals/EditPatientTrackedTime'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'

// Stable noop function to prevent useEffect dependency changes
const noop = () => {}

const TimeTrackedListingPage = () => {
  // toast alerts
  const notifyError = (msg: string) => toast.error(msg)
  const notifySuccess = (msg: string) => toast.success(msg)

  const [timeTrackedData, setTimeTrackedData] = useState([])
  const [loading, setLoading] = useState(false)
  const {currentUser} = useReduxAuth()
  const roleId = currentUser?.role_id

  // MA Doctor Dropdown
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined
  const setMaSelectedDoctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.setSelectedDoctorId : noop
  const [maDoctors, setMaDoctors] = useState<Array<{id: number; full_name: string}>>([])

  // Fetch all doctors for MA
  useEffect(() => {
    if (roleId === USER_ROLES.MEDICAL_ASSISTANT) {
      axios
        .get(API_ENDPOINTS.GET_ALL_DOCTORS())
        .then((resp) => {
          const fetchedDoctors = resp.data.data?.doctors || resp.data.data || []
          setMaDoctors(fetchedDoctors)
          if (fetchedDoctors.length > 0 && !maSelectedDoctorId) {
            setMaSelectedDoctorId(fetchedDoctors[0].id)
          }
        })
        .catch((err) => {
          setMaDoctors([])
        })
    }
  }, [roleId, maSelectedDoctorId, setMaSelectedDoctorId])

  // function to get data
  const fetchData = useCallback(async () => {
    setLoading(true)
    let response

    // Determine doctor ID: For MA use selected doctor, otherwise use current user's doctor_id or id
    const getDoctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
      ? maSelectedDoctorId
      : currentUser.doctor_id ?? currentUser.id

    try {
      response = await axios.get(API_ENDPOINTS.GET_ALL_TIME_TRACKED_ENTRIES(getDoctorId))

      const items = response?.data?.data?.timeTrackedEntries || []
      setTimeTrackedData(items)
    } catch (error: any) {
      notifyError(error?.response?.data?.message)
    } finally {
      setLoading(false)
    }
  }, [currentUser, roleId, maSelectedDoctorId])

  // fetch data
  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Function to filter headers based on user permissions
  const filteredHeaders = TIME_TRACKED_HEADERS.filter((header) =>
    header.allowedRoles.includes(currentUser?.role_id!)
  )

    // — Edit-entry modal state —
    const [showEditModal, setShowEditModal] = useState(false)
    const [rowToEdit, setRowToEdit] = useState<any>(null)
    

    // — Delete-confirmation state —
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [rowToDelete, setRowToDelete] = useState<any>(null)
    
  const handleAction = (action: {name: string}, row: any) => {
    if (action.name === 'Edit') {
      setRowToEdit(row)
      setShowEditModal(true)
    }
    if (action.name === 'Delete') {
      setRowToDelete(row)
      setShowDeleteModal(true)
    }
  }

  const handleSaveEdit = async (updatedData: any) => {
    if (!rowToEdit) return
    try {
      await axios.put(API_ENDPOINTS.UPDATE_TIME_TRACKED_ENTRY(rowToEdit.id), updatedData)
      notifySuccess('Time entry updated!')
      await fetchData()
    } catch (err: any) {
      notifyError(err?.response?.data?.message || 'Update failed')
    } finally {
      setShowEditModal(false)
      setRowToEdit(null)
    }
  }
  const handleCancelEdit = () => {
    setShowEditModal(false)
    setRowToEdit(null)
  }

  // — Handlers for Delete —
  const handleConfirmDelete = async () => {
    if (!rowToDelete) return
    try {
      await axios.delete(API_ENDPOINTS.DELETE_TIME_TRACKED_ENTRY(rowToDelete.id))
      notifySuccess('Time entry deleted!')
      await fetchData()
    } catch (err: any) {
      notifyError(err?.response?.data?.message || 'Delete failed')
    } finally {
      setShowDeleteModal(false)
      setRowToDelete(null)
    }
  }
  const handleCancelDelete = () => {
    setShowDeleteModal(false)
    setRowToDelete(null)
  }


  return (
    <>
    <MetaTags title={'Time Tracked'} />
      {/* Doctor Dropdown for MA */}
      {roleId === USER_ROLES.MEDICAL_ASSISTANT && (
        <div className='d-flex justify-content-end mb-3'>
          <div className='w-25'>
            <label htmlFor='doctorSelect' className='form-label'>
              Select Doctor
            </label>
            <select
              id='doctorSelect'
              className='form-select'
              value={maSelectedDoctorId ?? ''}
              onChange={(e) => {
                const selectedId = e.target.value ? parseInt(e.target.value) : undefined
                setMaSelectedDoctorId(selectedId)
              }}
              disabled={maDoctors.length === 0}
            >
            {maDoctors.length === 0 ? (
              <option value=''>-- No doctors available --</option>
            ) : (
              <>
                <option value=''>-- Select Doctor --</option>
                {maDoctors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.full_name}
                  </option>
                ))}
              </>
            )}
            </select>
          </div>
        </div>
      )}

      {roleId === USER_ROLES.MEDICAL_ASSISTANT && !maSelectedDoctorId ? (
        <div className='card'>
          <div className='card-body text-center py-10'>
            <p className='text-muted'>Please select a doctor to view time tracked entries.</p>
          </div>
        </div>
      ) : (
        <DataTableList
          title='Time Tracked Data'
          columns={filteredHeaders}
          data={timeTrackedData}
          actions={[
            {name: 'Edit', title: 'Edit'},
            {name: 'Delete', title: 'Delete'},
          ]}
          onAction={handleAction}
          showAddButton={false}
          isLoading={loading}
        />
      )}

        {/* Edit Modal */}
        <EditPatientTrackedTime
        show={showEditModal}
        initialData={rowToEdit}
        onSave={handleSaveEdit}
        onCancel={handleCancelEdit}
      />

      {/* Delete Confirmation */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  )
}

export default TimeTrackedListingPage
